import PropTypes from 'prop-types';
import { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

// project imports
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';

import { DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// assets
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);

  // Mobile only: the temporary drawer un-mounts (display:none) entirely
  // while closed, so there's no element inside it to attach a toggle to —
  // this one is `position:fixed` instead, rendered unconditionally, with
  // `left` tracking where the drawer's right edge currently is (0 when
  // collapsed/hidden, DRAWER_WIDTH when open).
  const mobileToggleButton = (
    <IconButton
      aria-label={drawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
      onClick={() => handlerDrawerOpen(!drawerOpen)}
      sx={{
        position: 'fixed',
        top: 72,
        left: drawerOpen ? DRAWER_WIDTH + 2 : 2,
        zIndex: 1300, // above both the fixed Header (1201) and the temporary Drawer's Modal (~1200)
        width: 22,
        height: 22,
        bgcolor: '#ffffff',
        color: '#5b4fe0',
        boxShadow: '0 2px 6px rgba(15,23,42,0.25)',
        opacity: 0.45,
        transition: 'opacity 0.15s ease, left 0.25s ease',
        '&:hover': { opacity: 1, bgcolor: '#ffffff' }
      }}
    >
      {drawerOpen ? <LeftOutlined style={{ fontSize: 11 }} /> : <RightOutlined style={{ fontSize: 11 }} />}
    </IconButton>
  );

  // Desktop only: previously an edge-attached floating pill straddling the
  // drawer/canvas boundary — moved to a fixed footer row inside the drawer
  // itself instead, so it no longer floats over the canvas.
  const desktopToggleButton = (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: drawerOpen ? 'flex-end' : 'center',
        alignItems: 'center',
        px: drawerOpen ? 1.5 : 0,
        py: 1.5,
        borderTop: '1px solid rgba(255,255,255,0.12)'
      }}
    >
      <IconButton
        aria-label={drawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        onClick={() => handlerDrawerOpen(!drawerOpen)}
        sx={{
          width: 32,
          height: 32,
          color: '#bfb9ef',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.10)', color: '#ffffff' }
        }}
      >
        {drawerOpen ? <LeftOutlined style={{ fontSize: 14 }} /> : <RightOutlined style={{ fontSize: 14 }} />}
      </IconButton>
    </Box>
  );

  // No explicit zIndex here on purpose: position:relative + an explicit
  // z-index together would make `nav` establish its own stacking context,
  // capping every descendant's z-index at that level regardless of what
  // they declare individually — which is exactly what let the mobile
  // temporary drawer's portaled modal backdrop paint over the mobile
  // toggle while open. Leaving z-index unset here lets that toggle's own
  // z-index compete directly against the header/backdrop. (Doesn't apply
  // to the desktop toggle any more — it's in normal flow now, not fixed.)
  return (
    <Box component="nav" sx={{ position: 'relative', flexShrink: { md: 0 } }} aria-label="mailbox folders">
      {!downLG ? (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{drawerContent}</Box>
            {desktopToggleButton}
          </Box>
        </MiniDrawerStyled>
      ) : (
        <>
          <Drawer
            container={container}
            variant="temporary"
            open={drawerOpen}
            onClose={() => handlerDrawerOpen(!drawerOpen)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                borderRight: 'none',
                background: 'linear-gradient(180deg, #070057 0%, #3e397ca3 100%)',
                // marginTop (not paddingTop) — see MiniDrawerStyled.js's paperOnlyStyle
                // for why: padding only pushes the content down while the box itself
                // still starts at y:0 behind the header. Height subtracts the same
                // 48px, or the margin just pushes the box 48px past the footer.
                marginTop: '56px', // must match theme.mixins.toolbar.minHeight (themes/index.jsx)
                // MUI hardcodes `.MuiDrawer-paper` to position:fixed + height:100%
                // for every variant, so height:100% resolves to the full viewport —
                // override it so the paper stops above the footer instead of
                // bleeding through behind it. Matches Footer.jsx's own minHeight
                // breakpoints (76px stacked on xs, 44px single-row from sm up) —
                // footer's own xs/sm switch, not this drawer's lg breakpoint.
                height: { xs: 'calc(100vh - 76px - 56px)', sm: 'calc(100vh - 44px - 56px)' },
                boxShadow: 'inherit'
              }
            }}
          >
            {drawerContent}
          </Drawer>
          {mobileToggleButton}
        </>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
